import type { Clock } from './clock';

/**
 * The dashboard's greeting, from the clock.
 *
 * It used to be the literal "Good morning", at every hour. It now reads the
 * time of day in the same zone as the date beside it in the header — the
 * reader's, by the decision recorded on `formatToday` in app-shell.tsx: that
 * date is "the day the person is standing in", not a scheduling datetime, and
 * a greeting is the same kind of thing. The two must agree; a header saying
 * Saturday over "Good afternoon" would be a second disagreement in place of
 * the first.
 *
 * `timeZone` exists for tests, which pin it so they hold on a machine in any
 * zone. The console passes none.
 */

export type PartOfDay = 'morning' | 'afternoon' | 'evening';

/** Morning from 5am to noon, afternoon to 6pm, evening otherwise. */
export function partOfDay(clock: Clock, timeZone?: string): PartOfDay {
  const hour = Number(
    new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hourCycle: 'h23',
      ...(timeZone ? { timeZone } : {}),
    }).format(clock.now()),
  );
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  return 'evening';
}

/** "Good afternoon, Owner" — or "Good afternoon" without a name. */
export function greeting(clock: Clock, firstName?: string, timeZone?: string): string {
  const base = `Good ${partOfDay(clock, timeZone)}`;
  return firstName ? `${base}, ${firstName}` : base;
}
